/**
 * ClaimLabelsControl - triage label chips for the claim detail header.
 *
 * Renders the claim's applied labels as Badge chips and (when the user
 * can edit) an inline "+ Label" popover to add/remove them. Labels are a
 * lightweight tag, so writes go through the no-reason
 * POST /claims/claims/:uuid/labels/ endpoint (useSetClaimLabels), not the
 * reason-gated attribute editor. Options come from the registry-backed
 * /claims/label-options/ endpoint.
 */

import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Popover } from '@base-ui/react/popover'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { useClaimLabelOptions, useSetClaimLabels } from '@/admin/hooks/use-claims'
import { toastApiError } from '@/shared/lib/api-error'
import { selectableLabelOptions } from '@/admin/lib/claim-label-options'

const HEX6 = /^#[0-9a-fA-F]{6}$/

/**
 * LabelChip - a GitHub-style triage tag. When the label carries a hex
 * colour the chip is tinted with it (translucent fill + colour text and
 * border); otherwise it falls back to a neutral chip. Colour is dynamic
 * data (a stored hex), so the inline style is the sanctioned exception.
 */
export function LabelChip({
  label,
  color,
  onRemove,
}: {
  label: string
  color?: string | null
  onRemove?: () => void
}) {
  const c = color && HEX6.test(color) ? color : null
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-light)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink-2)]"
      style={
        c ? { backgroundColor: `${c}1f`, borderColor: `${c}66`, color: c } : undefined
      }
    >
      {label}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${label} label`}
          className="-mr-0.5 ml-0.5 opacity-70 hover:opacity-100"
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  )
}

export function ClaimLabelsControl({
  claimUuid,
  labels,
  canEdit,
}: {
  claimUuid: string
  labels: string[]
  canEdit: boolean
}) {
  const { data: options = [] } = useClaimLabelOptions()
  const setLabels = useSetClaimLabels()
  const [open, setOpen] = useState(false)

  // The `labels` attribute is an opt-in, per-tenant config (seeded only for
  // tenants that use triage labels). When the tenant has no label options
  // configured the feature is off for them entirely - we neither show the
  // "+ Label" control nor its empty popover.
  // Retired options still name and colour a chip, but are never offered.
  const selectable = selectableLabelOptions(options)
  const hasLabelsConfigured = selectable.length > 0

  // Optimistic local state. Toggling several labels in quick succession
  // would otherwise each read the lagging `labels` prop (which only
  // updates after the mutation's refetch lands) and clobber each other.
  const [current, setCurrent] = useState<string[]>(labels)
  useEffect(() => {
    setCurrent(labels)
  }, [labels])

  const applied = new Set(current)
  const optionFor = (value: string) => options.find((o) => o.value === value)
  const labelFor = (value: string) => optionFor(value)?.label ?? value

  function commit(next: string[]) {
    setCurrent(next)
    setLabels.mutate(
      { uuid: claimUuid, labels: next },
      { onError: (err) => toastApiError(err, 'Failed to update labels') },
    )
  }

  function toggle(value: string) {
    commit(
      applied.has(value) ? current.filter((l) => l !== value) : [...current, value],
    )
  }

  const canAddLabels = canEdit && hasLabelsConfigured

  // Nothing to show: no labels applied and the user can't (or there's nothing
  // to) add. This hides the whole control for tenants without the feature.
  if (current.length === 0 && !canAddLabels) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {current.map((value) => (
        <LabelChip
          key={value}
          label={labelFor(value)}
          color={optionFor(value)?.color}
          onRemove={
            canEdit ? () => commit(current.filter((l) => l !== value)) : undefined
          }
        />
      ))}

      {canAddLabels ? (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger className="inline-flex items-center gap-1 rounded-full border-[0.5px] border-[var(--rule-strong)] px-2 py-0.5 text-[11px] text-[var(--ink-2)] transition-colors hover:bg-[var(--bg-light)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
            <Plus className="h-3 w-3" />
            Label
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Positioner side="bottom" align="start" sideOffset={6} className="isolate z-50">
              <Popover.Popup className="min-w-[200px] rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] p-1.5 shadow-[var(--shadow-float)]">
                {selectable.length === 0 ? (
                  <p className="px-2 py-1.5 text-[12px] text-[var(--ink-3)]">
                    No labels configured
                  </p>
                ) : (
                  selectable.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-1.5 text-[13px] text-[var(--ink)] hover:bg-[var(--bg-light)]"
                    >
                      <Checkbox
                        checked={applied.has(opt.value)}
                        onCheckedChange={() => toggle(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))
                )}
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
      ) : null}
    </div>
  )
}
